// VIBRANIAM-SPACE v2 - node-layout.js
// Deterministic geometry for a node box. ModuleNode draws with these numbers
// and DAGCanvas routes cables with them, so a parameter port dot always sits
// exactly on the row of the slider it drives.

import { NODE_TYPES, schemaOf } from '../engine/nodes/index.js';

export const NODE_WIDTH = 224;
export const HEADER_H = 30;
export const PORT_ROW_H = 20;
export const PORTS_PAD = 8;

export const CONTROL_H = {
  range: 32,
  select: 36,
  bool: 22,
  color: 36,
  forceKind: 36,
};

export const controlHeight = (field) => CONTROL_H[field.type || 'range'] ?? CONTROL_H.range;

/** Schema entries that get a visible control. */
export function controlsOf(node) {
  return schemaOf(node);
}

export function declaredPorts(node) {
  const def = NODE_TYPES[node.type];
  return {
    inputs: def?.inputs || [],
    outputs: def?.outputs || [],
  };
}

export function portsSectionHeight(node) {
  const { inputs, outputs } = declaredPorts(node);
  const rows = Math.max(inputs.length, outputs.length);
  return rows ? rows * PORT_ROW_H + 6 : 0;
}

export function controlsTop(node) {
  return HEADER_H + PORTS_PAD + portsSectionHeight(node);
}

export function nodeHeight(node, collapsed) {
  if (collapsed) return HEADER_H + PORTS_PAD + portsSectionHeight(node) + 8;
  const top = controlsTop(node);
  const fields = controlsOf(node);
  const body = fields.reduce((sum, f) => sum + controlHeight(f), 0);
  return top + (fields.length ? 6 + body : 0) + 10;
}

/**
 * Local offset of a port inside the node box.
 * @param {'in'|'out'} dir
 */
export function portOffset(node, portId, dir, collapsed = false) {
  const { inputs, outputs } = declaredPorts(node);

  if (dir === 'out') {
    const idx = outputs.findIndex((p) => p.id === portId);
    if (idx < 0) return null;
    return { x: NODE_WIDTH, y: HEADER_H + PORTS_PAD + 10 + idx * PORT_ROW_H };
  }

  const declaredIdx = inputs.findIndex((p) => p.id === portId);
  if (declaredIdx >= 0) {
    return { x: 0, y: HEADER_H + PORTS_PAD + 10 + declaredIdx * PORT_ROW_H };
  }

  if (!portId.startsWith('p:')) return null;
  const key = portId.slice(2);

  // Collapsed nodes fold every parameter port onto one point on the left edge.
  if (collapsed) {
    return { x: 0, y: HEADER_H + PORTS_PAD + portsSectionHeight(node) };
  }

  const fields = controlsOf(node);
  let y = controlsTop(node) + 6;
  for (const f of fields) {
    if (f.key === key) return { x: 0, y: y + 9 };
    y += controlHeight(f);
  }
  return null;
}
